/* ═══════════════════════════════════════════════════
   SCHEMES — Filter, Search & Editor Support
   ═══════════════════════════════════════════════════ */

if (!customElements.get("schemes-component")) {
  class SchemesComponent extends HTMLElement {
    constructor() {
      super();
      this.filterBtns = this.querySelectorAll("[data-sc-filters] .sc-filter-btn");
      this.searchInput = this.querySelector("[data-sc-search]");
      this.grid = this.querySelector("[data-sc-grid]");
      this.noResults = this.querySelector("[data-sc-no-results]");
      this.cards = this.grid
        ? Array.from(this.grid.querySelectorAll(".sc-card"))
        : [];
      this.activeFilter = "all";
      this.searchQuery = "";
      this._debounceTimer = null;
    }

    connectedCallback() {
      this.bindFilters();
      this.bindSearch();
      this.bindCardTap();
    }

    /* ── Filter pill click ── */
    bindFilters() {
      this.filterBtns.forEach((btn) => {
        btn.addEventListener("click", () => {
          const filter = btn.dataset.filter;
          if (filter === this.activeFilter) return;

          this.activeFilter = filter;

          this.filterBtns.forEach((b) => {
            b.classList.remove("active");
            b.setAttribute("aria-selected", "false");
          });
          btn.classList.add("active");
          btn.setAttribute("aria-selected", "true");

          this.applyFilters();
        });
      });
    }

    /* ── Search input with debounce ── */
    bindSearch() {
      if (!this.searchInput) return;

      this.searchInput.addEventListener("input", () => {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          this.searchQuery = this.searchInput.value.trim().toLowerCase();
          this.applyFilters();
        }, 200);
      });
    }

    /* ── Mobile tap to toggle overlay ── */
    bindCardTap() {
      if (!("ontouchstart" in window)) return;

      this.cards.forEach((card) => {
        const wrap = card.querySelector(".sc-card-img-wrap");
        if (!wrap) return;

        wrap.addEventListener("click", (e) => {
          /* If a level button was clicked, let it navigate */
          if (e.target.closest(".sc-level-btn")) return;

          /* Toggle tapped state */
          const wasTapped = card.classList.contains("sc-card--tapped");

          /* Close all other tapped cards */
          this.cards.forEach((c) => c.classList.remove("sc-card--tapped"));

          if (!wasTapped) {
            card.classList.add("sc-card--tapped");
            e.preventDefault();
          }
        });
      });

      /* Close overlay when tapping outside */
      document.addEventListener("click", (e) => {
        if (!e.target.closest(".sc-card")) {
          this.cards.forEach((c) => c.classList.remove("sc-card--tapped"));
        }
      });
    }

    /* ── Combined filter + search logic ── */
    applyFilters() {
      const visible = [];

      this.cards.forEach((card) => {
        const type = (card.dataset.cardType || "").toLowerCase();
        const title = (card.dataset.cardTitle || "").toLowerCase();

        const matchesType =
          this.activeFilter === "all" || type === this.activeFilter;
        const matchesSearch =
          !this.searchQuery || title.includes(this.searchQuery);

        if (matchesType && matchesSearch) {
          card.classList.remove("sc-card--hidden");
          visible.push(card);
        } else {
          card.classList.add("sc-card--hidden");
        }
      });

      /* Update animation order */
      visible.forEach((card, i) => {
        card.style.setProperty("--animation-order", i + 1);
      });

      /* Show/hide no results message */
      if (this.noResults) {
        this.noResults.style.display = visible.length === 0 ? "block" : "none";
      }
      if (this.grid) {
        this.grid.style.display = visible.length === 0 ? "none" : "";
      }

      /* Replay entrance animation */
      this.replayAnimation(visible);
    }

    /* ── Replay scroll-trigger animation ── */
    replayAnimation(visibleCards) {
      visibleCards.forEach((card) => {
        const animClass = [...card.classList].find((c) =>
          c.startsWith("animate--")
        );
        if (!animClass) return;
        card._scAnimClass = animClass;
        card.classList.add("sc-card--pre-anim");
        card.classList.remove("m-scroll-trigger", animClass);
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          visibleCards.forEach((card) => {
            if (card._scAnimClass) {
              card.classList.remove("sc-card--pre-anim");
              card.classList.add("m-scroll-trigger", card._scAnimClass);
            }
          });
        });
      });
    }

    /* ── Reset for Shopify editor re-renders ── */
    reset() {
      this.filterBtns = this.querySelectorAll("[data-sc-filters] .sc-filter-btn");
      this.searchInput = this.querySelector("[data-sc-search]");
      this.grid = this.querySelector("[data-sc-grid]");
      this.noResults = this.querySelector("[data-sc-no-results]");
      this.cards = this.grid
        ? Array.from(this.grid.querySelectorAll(".sc-card"))
        : [];

      /* Reset filter to "All" */
      this.filterBtns.forEach((b) => {
        b.classList.remove("active");
        b.setAttribute("aria-selected", "false");
      });
      const allBtn = this.querySelector('[data-filter="all"]');
      if (allBtn) {
        allBtn.classList.add("active");
        allBtn.setAttribute("aria-selected", "true");
      }
      this.activeFilter = "all";

      /* Reset search */
      if (this.searchInput) this.searchInput.value = "";
      this.searchQuery = "";

      /* Show all cards */
      this.cards.forEach((c) => c.classList.remove("sc-card--hidden"));
      if (this.noResults) this.noResults.style.display = "none";
      if (this.grid) this.grid.style.display = "";

      this.bindFilters();
      this.bindSearch();
      this.bindCardTap();
    }
  }

  customElements.define("schemes-component", SchemesComponent);
}

/* ── Shopify editor: re-initialize on section re-render ── */
if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target;
    const component = section.querySelector("schemes-component");
    if (component) component.reset();
  });
}
